import CESAppInfo from "../CESAppInfo";
import { ValueElement } from "./Base";
import { TextInputElement, TextInputType } from "./TextInput";

declare let grecaptcha: any;

declare global {
  interface Window {
    onCaptchaExpire: () => void;
    onCaptchaSubmit: () => void;
  }
}

export default class RecaptchaElement extends ValueElement {
  private static singleton: RecaptchaElement;

  private instance: HTMLDivElement;

  private constructor(name: string, classes: string[] = []) {
    super(name);
    this.instance = document.createElement("div");

    const captcha = document.createElement("div");
    captcha.setAttribute("id", name);
    captcha.classList.add(...classes);
    captcha.dataset.sitekey = CESAppInfo.reCaptchakey;
    captcha.dataset.callback = "onCaptchaSubmit";
    captcha.setAttribute("data-expired-callback", "onCaptchaExpire");
    this.instance.appendChild(captcha);

    const { element: captchaField } = new TextInputElement(
      `${name}-field`,
      "",
      TextInputType.Hidden
    );
    this.instance.appendChild(captchaField);

    window.onCaptchaExpire = this.resetTokenValue;
    window.onCaptchaSubmit = this.submit;
  }

  public static getInstance(
    name: string = "recaptcha",
    classes: string[] = ["g-recaptcha", "input-container"]
  ): RecaptchaElement {
    if (!RecaptchaElement.singleton || RecaptchaElement.singleton.name !== name) {
      RecaptchaElement.singleton = new RecaptchaElement(name, classes);
    }

    const dashIdx = CESAppInfo.locale.indexOf("-");
    const hl = CESAppInfo.locale.substring(0, dashIdx);
    const recaptchaScriptId = "recaptchaScript";

    if (!document.getElementById(recaptchaScriptId)) {
      const script = document.createElement("script");
      script.id = recaptchaScriptId;
      script.src = `https://www.google.com/recaptcha/api.js?hl=${hl}`;
      document.querySelector("head")!.appendChild(script);
    }

    return RecaptchaElement.singleton;
  }

  get element(): HTMLElement {
    return this.instance;
  }

  get value(): any {
    const input = this.instance.querySelector("input");
    return input?.value || input?.getAttribute("value");
  }

  // Captcha tokens can't be assigned from form prefill — no-op setter keeps
  // form setValues() from throwing (matches RecaptchaV3/Enterprise).
  set value(_val: any) {}

  submit = () => {
    const element = document.getElementById(this.name);
    const captchaToken = grecaptcha.getResponse();
    element?.setAttribute("captchaVerified", "true");
    document
      .getElementById(`${this.name}-field`)!
      .setAttribute("value", captchaToken);
    this.onChange();
    return captchaToken;
  };

  resetTokenValue = () => {
    document.getElementById(`${this.name}-field`)!.setAttribute("value", "");
  };

  reset = () => {
    if (document.getElementById(this.name)) {
      document
        .getElementById(this.name)!
        .setAttribute("captchaVerified", String(false));
      this.resetTokenValue();
      grecaptcha.reset();
    }
  };

  isApplied = () => !!document.getElementById(this.name);

  isVerified = () =>
    document.getElementById(this.name)!.getAttribute("captchaVerified") ===
    "true";

  dispose(): void {}

  disable(): void {}
}
